import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { emptySplitApi, RootState } from 'shared/store/store'
import { AdditionalRows, TimeInterval } from '../models/types'
import { BudgetAssumptionsCategory } from '../models/budgetAssumptionsCategory'
import { BudgetModel } from '../models/budgetModel'
import { BudgetDepartmentModel } from '../models/budgetDepartmentModel'
import {
  GET_BUDGET_APPROVAL_DEADLINE,
  GET_BUDGET_COMMENTS,
  GET_BUDGET_DEPARTMENTS,
  GET_BUDGET_LINES,
  GET_BUDGET_SPECIFICATION,
} from 'shared/constants/storeTags'

const initialState = {
  selectedBudget: null as BudgetModel | null,
  currentPageIndex: null as number | null,
  timeInterval: TimeInterval.YEAR,
  additionalRows: [] as AdditionalRows[],
  selectedAssumptionsCategory: 'Alle kategorier' as BudgetAssumptionsCategory,
  expandedBudgetLines: [] as string[],
  hideAccountNo: false,
  selectedDepartment: {
    departmentName: 'Alle avdelinger',
    departmentNo: -1,
  } as BudgetDepartmentModel | null,
}

export const updateDepartmentThunk = createAsyncThunk(
  'budgetSlice/setSelectedDepartment',
  (department: BudgetDepartmentModel, thunkApi) => {
    thunkApi.dispatch(budgetSlice.actions.setSelectedDepartment(department))
    thunkApi.dispatch(
      emptySplitApi.util.invalidateTags([
        GET_BUDGET_SPECIFICATION,
        GET_BUDGET_LINES,
      ])
    )
  }
)

export const setSelectedBudgetThunk = createAsyncThunk(
  'budgetSlice/setSelectedBudget',
  (budget: BudgetModel, thunkApi) => {
    thunkApi.dispatch(budgetSlice.actions.setSelectedBudget(budget))
    // Reset department selection
    thunkApi.dispatch(
      budgetSlice.actions.setSelectedDepartment({
        departmentName: 'Alle avdelinger',
        departmentNo: -1,
      } as BudgetDepartmentModel)
    )
    // Invalidate budget lines, specification and departments
    thunkApi.dispatch(
      emptySplitApi.util.invalidateTags([
        GET_BUDGET_SPECIFICATION,
        GET_BUDGET_LINES,
        GET_BUDGET_DEPARTMENTS,
        GET_BUDGET_APPROVAL_DEADLINE,
        GET_BUDGET_COMMENTS,
      ])
    )
  }
)

const budgetSlice = createSlice({
  name: 'budget',
  initialState,
  reducers: {
    setCurrentPageIndex: (state, action: PayloadAction<number>) => {
      state.currentPageIndex = action.payload
    },
    setTimeInterval: (state, action: PayloadAction<TimeInterval>) => {
      state.timeInterval = action.payload
    },
    setAdditionalRows: (state, action: PayloadAction<AdditionalRows[]>) => {
      state.additionalRows = action.payload
    },
    setSelectedAssumptionsCategory: (
      state,
      action: PayloadAction<BudgetAssumptionsCategory>
    ) => {
      state.selectedAssumptionsCategory = action.payload
    },
    setSelectedBudget: (state, action: PayloadAction<BudgetModel>) => {
      state.selectedBudget = action.payload
    },

    setExpandedBudgetLines: (state, action: PayloadAction<string[]>) => {
      state.expandedBudgetLines = action.payload
    },
    addExpandedBudgetLine: (state, action: PayloadAction<string>) => {
      state.expandedBudgetLines.push(action.payload)
    },
    removeExpandedBudgetLine: (state, action: PayloadAction<string>) => {
      state.expandedBudgetLines = state.expandedBudgetLines.filter(
        (id) => id !== action.payload
      )
    },
    toggleHideAccountNo: (state) => {
      state.hideAccountNo = !state.hideAccountNo
    },
    setSelectedDepartment: (
      state,
      action: PayloadAction<BudgetDepartmentModel>
    ) => {
      state.selectedDepartment = action.payload
    },
    resetBudgetState: () => {
      return initialState
    },
  },
})

export const getCurrentPageIndex = (state: RootState) =>
  state.budget.currentPageIndex

export const getTimeInterval = (state: RootState) => state.budget.timeInterval

export const getAdditionalRows = (state: RootState) =>
  state.budget.additionalRows

export const getSelectedAssumptionsCategory = (state: RootState) =>
  state.budget.selectedAssumptionsCategory

export const getSelectedBudget = (state: RootState) =>
  state.budget.selectedBudget

export const getBudgetStatus = (state: RootState) =>
  state.budget.selectedBudget?.status

export const getExpandedBudgetLines = (state: RootState) =>
  state.budget.expandedBudgetLines

export const getHideAccountNo = (state: RootState) => state.budget.hideAccountNo

export const getSelectedDepartment = (state: RootState) =>
  state.budget.selectedDepartment

export const {
  setCurrentPageIndex,
  setTimeInterval,
  setAdditionalRows,
  setSelectedAssumptionsCategory,
  setSelectedBudget,
  setExpandedBudgetLines,
  addExpandedBudgetLine,
  removeExpandedBudgetLine,
  toggleHideAccountNo,
  setSelectedDepartment,
  resetBudgetState,
} = budgetSlice.actions

export default budgetSlice
